/**
 * Application constants for Thaklis
 */

// Dynamic API configuration - Single source of truth
const determineApiBaseUrl = () => {
  // Highest priority: runtime override injected by hosting layer (no rebuild needed)
  try {
    if (typeof window !== 'undefined' && window.__API_BASE_URL && typeof window.__API_BASE_URL === 'string' && window.__API_BASE_URL.trim().length > 0) {
      const raw = window.__API_BASE_URL.trim().replace(/\/$/, '');
      // Normalize runtime override: if host starts with 'app', map to 'api' (e.g., appd -> apid)
      try {
        const u = new URL(raw);
        const parts = u.hostname.split('.');
        if (parts.length > 0 && /^app/i.test(parts[0])) {
          parts[0] = parts[0].replace(/^app/i, 'api');
          const normalizedHost = parts.join('.');
          const normalized = `${u.protocol}//${normalizedHost}${u.port ? ':' + u.port : ''}`;
          return normalized;
        }
      } catch (_) { /* ignore parse errors and return raw */ }
      return raw;
    }
  } catch (_) { /* ignore */ }

  // Second priority: environment variable (build-time configuration)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }

  // Third priority: environment-specific detection
  if (typeof window !== 'undefined' && window.location) {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;

    // Local development: Use empty baseURL so requests go through setupProxy middleware
    // setupProxy.js will intercept /v1 and /api requests and proxy them to localhost:8080
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      console.log('🔧 [API CONFIG] Development mode detected. Using setupProxy middleware to route requests to http://localhost:8080');
      return ''; // Empty baseURL = requests will be relative and go through proxy
    }

    // Production/staging:
    // If running on app subdomains, map to corresponding api subdomains.
    // Examples:
    //  - appd.thaklis.com -> apid.thaklis.com
    //  - app.thaklis.com  -> api.thaklis.com
    //  - appX.example.com -> apiX.example.com (generic: replace leading 'app' with 'api')
    try {
      const parts = hostname.split('.');
      if (parts.length > 1 && /^app/i.test(parts[0])) {
        const apiSub = parts[0].replace(/^app/i, 'api');
        const apiHost = [apiSub, ...parts.slice(1)].join('.');
        const apiUrl = `${protocol}//${apiHost}`;
        console.log('🔧 [API CONFIG] Production mode detected. Using API subdomain:', apiUrl);
        return apiUrl;
      }
    } catch (_) { /* ignore and fall back to same-origin */ }

    // Default: use same host with standard ports (no port specified)
    // Works for both HTTP (80) and HTTPS (443)
    const sameOriginUrl = `${protocol}//${hostname}`;
    console.log('🔧 [API CONFIG] Using same-origin API:', sameOriginUrl);
    return sameOriginUrl;
  }

  // Fallback for server-side rendering or edge cases
  console.log('🔧 [API CONFIG] Using fallback API: http://localhost:8080');
  return 'http://localhost:8080';
};

// API endpoints
export const API_BASE_URL = determineApiBaseUrl();
export const API_URL = API_BASE_URL; // For backward compatibility

// Standard API paths (without trailing slash)
export const API_PATHS = {
  BASE: API_BASE_URL,
  API: `${API_BASE_URL}/api`,
  V1: `${API_BASE_URL}/v1`,
  PUBLIC: `${API_BASE_URL}/api/public`,
  AUTH: `${API_BASE_URL}/v1/auth`,
  FILE: `${API_BASE_URL}/api/file`,
  TICKETS: `${API_BASE_URL}/api/tickets`,
  WORKFLOW: `${API_BASE_URL}/v1/workflow`,
  WORKFLOWS_API: `${API_BASE_URL}/api/workflows`,
  INVOICE: `${API_BASE_URL}/api/invoice`,
  WALLET: `${API_BASE_URL}/api/wallet`,
  ORGANISATION: `${API_BASE_URL}/api/organisations`,
  ORGANIZATIONS: `${API_BASE_URL}/api/organizations`, // US spelling alternative
  DOCUMENTS: `${API_BASE_URL}/v1/users`,
  NOTIFICATIONS: `${API_BASE_URL}/api/notifications`,
  DOCUMENT_DOWNLOAD: (uid, documentType) => {
    // Check if this is a dynamic service field (UUID pattern)
    const isDynamicServiceField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(documentType);
    
    if (isDynamicServiceField) {
      console.log('Dynamic service field detected in constants.DOCUMENT_DOWNLOAD, cannot construct employee document URL for UUID documentType:', documentType);
      // Return null for dynamic service fields - they should use file path URLs instead
      return null;
    }
    // Guard: uid must be present and not 'undefined' or 'null'
    if (!uid || uid === 'undefined' || uid === 'null') {
      console.warn('constants.DOCUMENT_DOWNLOAD called without a valid uid. Returning null. documentType:', documentType, 'uid:', uid);
      return null;
    }
    
    return `${API_BASE_URL}/v1/users/${uid}/documents/${documentType}/download`;
  }
};

/**
 * Helper to get API URL for a specific endpoint
 * @param {string} path - The API path without leading slash
 * @param {string} baseType - The base path type ('API', 'V1', etc)
 * @returns {string} Full API URL
 */
export const getApiUrl = (path, baseType = 'API') => {
  const base = API_PATHS[baseType] || API_PATHS.BASE;
  return `${base}/${path}`;
};

/**
 * Returns full base URL including scheme and hostname
 * @returns {string} Base URL
 */
export const getBaseUrl = () => API_BASE_URL;

// Ticket status constants
export const TICKET_STATUS = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CLOSED: 'CLOSED',
  REJECTED: 'REJECTED',
  INCOMPLETE: 'INCOMPLETE'
};

// Field types
export const FIELD_TYPES = {
  TEXT: 'TEXT',
  NUMBER: 'NUMBER',
  DATE: 'DATE',
  SELECT: 'SELECT',
  MULTI_SELECT: 'MULTI_SELECT',
  CHECKBOX: 'CHECKBOX',
  FILE: 'FILE',
  TEXTAREA: 'TEXTAREA'
};

// Validation types
export const VALIDATION_TYPES = {
  REQUIRED: 'REQUIRED',
  MIN_LENGTH: 'MIN_LENGTH',
  MAX_LENGTH: 'MAX_LENGTH',
  PATTERN: 'PATTERN',
  MIN_VALUE: 'MIN_VALUE',
  MAX_VALUE: 'MAX_VALUE',
  EMAIL: 'EMAIL',
  PHONE: 'PHONE'
};

// User roles
export const USER_ROLES = {
  ADMIN: 'ADMIN',
  MANAGER: 'MANAGER',
  AGENT: 'AGENT',
  CUSTOMER: 'CUSTOMER'
};

// Pagination defaults
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 20, 50, 100]
};

// Date formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM DD, YYYY',
  DISPLAY_WITH_TIME: 'MMM DD, YYYY HH:mm',
  API: 'YYYY-MM-DD',
  API_WITH_TIME: 'YYYY-MM-DDTHH:mm:ss'
};

// Workflow constants
export const WORKFLOW_TYPES = {
  INSIDE: 'INSIDE',
  OUTSIDE: 'OUTSIDE'
};

// Dependency types
export const DEPENDENCY_TYPES = {
  FINISH_TO_START: 'FINISH_TO_START',
  START_TO_START: 'START_TO_START',
  FINISH_TO_FINISH: 'FINISH_TO_FINISH',
  START_TO_FINISH: 'START_TO_FINISH'
};
